import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ScanLine, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { createCustody, custodyReceiptUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Custody() {
  const { user, isAdmin, loading } = useAuth();
  const [f, setF] = useState({
    consegnante: "", ricevente: "", document_description: "",
    num_pages: 1, condition_notes: "", packaging_notes: "",
  });
  const [saved, setSaved] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await createCustody(f);
      setSaved(r);
      toast.success(`Verbale creato: ${r.tracking_code}`);
    } catch {
      toast.error("Errore nella creazione del verbale");
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      {!loading && !isAdmin && (
        <main className="flex-1 flex items-center justify-center px-6" data-testid="custody-forbidden">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Strumento riservato</h1>
            <p className="text-sm text-slate-600 mt-2">Il Registro di Custodia è disponibile solo agli amministratori.</p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
      )}
      {isAdmin && (
      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div className="overline mb-2 flex items-center gap-2"><ScanLine size={14} /> Document Custody</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Verbale di acquisizione</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Registra la consegna di un documento originale al consulente. Il sistema genera un
          verbale PDF con codice identificativo e QR di tracciamento — utilizzabile come
          catena di custodia forense.
        </p>

        <form onSubmit={save} className="border hairline bg-white p-6 lg:p-10 mt-8 grid md:grid-cols-2 gap-6">
          <Field label="Consegnante"><input required value={f.consegnante} onChange={(e) => setF({ ...f, consegnante: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-consegnante" /></Field>
          <Field label="Ricevente"><input required value={f.ricevente} onChange={(e) => setF({ ...f, ricevente: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-ricevente" /></Field>
          <div className="md:col-span-2">
            <Field label="Descrizione del documento">
              <textarea required value={f.document_description} onChange={(e) => setF({ ...f, document_description: e.target.value })} rows={2} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-desc" />
            </Field>
          </div>
          <Field label="Numero di pagine"><input type="number" min="1" required value={f.num_pages} onChange={(e) => setF({ ...f, num_pages: Number(e.target.value) })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-pages" /></Field>
          <Field label="Condizioni apparenti"><input value={f.condition_notes} onChange={(e) => setF({ ...f, condition_notes: e.target.value })} placeholder="Piegature, macchie, strappi..." className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-condition" /></Field>
          <div className="md:col-span-2">
            <Field label="Modalità di confezionamento">
              <input value={f.packaging_notes} onChange={(e) => setF({ ...f, packaging_notes: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="cust-packaging" />
            </Field>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors" data-testid="cust-submit">
              Genera verbale
            </button>
          </div>
        </form>

        {saved && (
          <div className="border hairline bg-white p-6 mt-6" data-testid="custody-result">
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <div className="overline mb-1">Verbale generato</div>
                <div className="font-mono text-lg" data-testid="custody-tracking">{saved.tracking_code}</div>
                <p className="text-sm text-slate-600 mt-2">
                  Conservare il codice per il tracciamento. Il PDF include il QR di identificazione.
                </p>
              </div>
              <a href={custodyReceiptUrl(saved.id)} target="_blank" rel="noreferrer" data-testid="cust-download"
                className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-4 py-2 rounded-sm text-sm">
                <Download size={16} /> Scarica PDF
              </a>
            </div>
          </div>
        )}
      </main>
      )}
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="overline mb-2">{label}</div>{children}</label>;
}
